<template lang="pug">
.product-pricing
  CardMinimal(:title="$t('products.prices')", :key="pricesKey")
    .price(v-for="(price, index) in data.prices", :key="index", :class="`${priceError === index ? 'error' : ''}`").p-a-4.d-f.ai-c
      template(v-if="price.deleted")
        .status
          i.mdi.mdi-trash-can-outline.red
          | {{ $t('products.removing') }}
      template(v-else)
        .status
          i.mdi(:class="`mdi-${price.active ? 'check-circle green' : 'pause-circle orange'}`")
          | {{ price.active ? $t('products.active') : $t('products.paused') }}
      .pricing
        .input-dropdown
          FormInput(size="1", type="number", :disabled="edit && price.type === 'recurring' && typeof price.edited !== 'undefined'", :min="1", v-model="data.prices[index].amount", @input="setAmount(index)").center-text.small
          FormDropdown(:items="currencies", :disabled="edit && typeof price.edited !== 'undefined'", :value="price.currency", @input="setCurrency(index, $event)").small
      .subscription(v-if="subscriptions").p-l-4
        FormDropdown(:items="priceTypes", :value="price.type", :disabled="edit && typeof price.edited !== 'undefined'", @input="setPriceType(index, $event)").medium
      .dots-wrapper.ml-a.p-l-4
        Dots(ref="dots").left
          ul
            template(v-if="price.deleted")
              li.red(@click="undoRemove(index)")
                i.mdi.mdi-trash-can-outline
                | {{ $t('products.undo_remove') }}
            template(v-else)
              li.red(@click="remove(index)")
                i.mdi.mdi-trash-can-outline
                | {{ $t('products.remove') }}
              li(:class="`${price.active ? 'orange' : 'green'}`", @click="toggleActive(index)")
                i.mdi(:class="`mdi-${ price.active ? 'pause-circle' : 'check-circle'}`")
                | {{ price.active ? $t('products.pause') : $t('products.activate') }}
    .p-a-4.d-f.ai-c.jc-c(@click="addNewPrice(false)" slot="footerButton", :class="`${data.prices.length === 0 ? 'all-round' : ''} ${priceError === true ? 'error' : ''}`")
      i.mdi.mdi-plus-circle
      span {{ data.prices.length === 0 ? $t('products.add_a_price') : $t('products.add_additional_price') }}
</template>

<script>

import Dots from '@/components/Dots.vue';

export default {
  components: {
    Dots,
  },
  props: {
    priceRequired: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: () => ({}),
    },
    subscriptions: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    priceTypes() {
      return [
        { value: 'one_time', string: this.$t('products.per_piece') },
        { value: 'recurring', string: this.$t('products.each_month') },
      ];
    },
    /**
     * Return the default currency for this account
     * @return {?String}
     */
    defaultCurrency() {
      return this.$store.getters['AccountManager/defaultCurrency'];
    },
    /**
     * Return a list of currencies available
     * @returns {Array}
     */
    currencies() {
      return this.$store.getters['AccountManager/currencies'];
    },
  },
  methods: {
    /**
     * Add a new price to the array
     */
    addNewPrice(active = false) {
      const { defaultCurrency } = this;
      const price = {
        currency: defaultCurrency || '',
        amount: defaultCurrency === 'usd' ? '4.9' : '49',
        active: (this.priceRequired && (this.data.prices.length === 0 || this.data.prices.filter(p => p.active && !p.deleted).length === 0)) || this.data.prices.length === 0 ? true : active,
      };

      if (this.subscriptions) {
        Object.assign(price, {
          type: 'one_time',
          recurring: { interval: 'month' },
        });
      }

      if (this.edit) {
        Object.assign(price, {
          product_id: this.editData.id,
        });
      }

      this.priceError = null;

      this.data.prices.push(price);
    },
    setCurrency(index, { value }) {
      this.data.prices[index].currency = value;
      if (typeof this.data.prices[index].edited !== 'undefined') {
        this.data.prices[index].edited = true;
      }
    },
    setPriceType(index, { value }) {
      this.data.prices[index].type = value;
      if (typeof this.data.prices[index].edited !== 'undefined') {
        this.data.prices[index].edited = true;
      }
    },
    toggleActive(index) {
      this.data.prices[index].active = !this.data.prices[index].active;
      if (typeof this.data.prices[index].edited !== 'undefined') {
        this.data.prices[index].edited = true;
      }
      this.$refs.dots[index].close();
    },
    remove(index) {
      if (typeof this.data.prices[index].deleted !== 'undefined') {
        this.data.prices[index].deleted = true;
        this.pricesKey += 1;
      } else {
        this.data.prices.splice(index, 1);
      }
    },
    undoRemove(index) {
      this.data.prices[index].deleted = false;
      this.pricesKey += 1;
    },
    setAmount(index) {
      if (typeof this.data.prices[index].edited !== 'undefined') {
        this.data.prices[index].edited = true;
      }
    },
    async validate() {
      this.priceError = null;
      for (const [index, price] of this.data.prices.entries()) {
        this.data.prices[index].amount = `${+price.amount.toString()}`;
        if (price.amount === 0 || `${price.amount}`.trim() === '' || +price.amount.toString() === '0' || +price.amount.toString() === 0) {
          this.priceError = index;
          return false;
        }
      }

      if (this.priceRequired && (this.data.prices.length === 0 || this.data.prices.filter(p => p.active && !p.deleted).length === 0)) {
        this.priceError = true;
        return false;
      }

      return true;
    },
    async update() {
      const { prices } = this.data;

      const newPrices = prices.filter(price => typeof price.edited === 'undefined' && typeof price.deleted === 'undefined');
      const updatePrices = prices.filter(price => price.edited && !price.deleted);
      const deletePrices = prices.filter(price => price.deleted);

      let failedUpdate = false;

      for (let i = 0; i < newPrices.length; i += 1) {
        // eslint-disable-next-line
        const result = await this.$store.dispatch('Price/createPrice', newPrices[i]);
        if (!result) {
          failedUpdate = true;
        }
      }

      for (let i = 0; i < updatePrices.length; i += 1) {
        // eslint-disable-next-line
        let data = {
          id: updatePrices[i].id,
          payload: {
            active: updatePrices[i].active,
          },
        };

        if (updatePrices[i].type === 'one_time') {
          data.payload.amount = updatePrices[i].amount;
        }

        // eslint-disable-next-line
        const result = await this.$store.dispatch('Price/updatePrice', data);
        if (!result) {
          failedUpdate = true;
        }
      }

      for (let i = 0; i < deletePrices.length; i += 1) {
        // eslint-disable-next-line
        const result = await this.$store.dispatch('Price/deletePrice', deletePrices[i].id);
        if (!result) {
          failedUpdate = true;
        }
      }

      if (failedUpdate) {
        // eslint-disable-next-line
        return false;
      }

      return true;
    },
    /*
    reset() {
      if (this.editData && this.edit) {
        this.data.prices = this.editData.prices ? this.editData.prices.map(price => Object.assign(price, { edited: false, deleted: false })) : [];
        this.pricesKey += 1;
      }
    },
    */
  },
  watch: {
    'data.prices': {
      deep: true,
      handler(data) {
        this.$emit('data', data);
      },
    },
  },
  data() {
    return {
      priceError: null,
      pricesKey: 0,
      data: {
        prices: [],
      },
    };
  },
  mounted() {
    if (this.editData && this.edit && this.editData.prices !== null && this.editData.prices.length > 0) {
      this.data.prices = this.editData.prices.map(price => Object.assign(price, { edited: false, deleted: false }));
    }
  },
};
</script>

<style lang="scss" scoped>
.add-new-price {
  background-color: white;
  border-radius: 0 0 5px 5px;
  color: #0360FF;
  font-size: 1rem;
  transition: background-color .3s;

  &:hover {
    cursor: pointer;
    background-color: #EFF5FF;
  }

  i {
    font-size: 1.5rem;
    margin-right: .5rem;
  }
}

.card-minimal .card-button > div.error {
  background-color: rgba(#D0021B, .2);
}

.price {
  flex-wrap: wrap;
  border-top: 1px solid #E7E9EC;

  .error {
    background-color: rgba(#D0021B, .2);
  }

  &:first-child {
    border-top: 0;

    &.error {
      border-radius: 10px 10px 0 0;
    }
  }

  .status {
    order: 0;
    align-items: center;
    display: flex;
    font-size: 16px;
    min-width: 110px;
    width: 50%;
    line-height: 30px;
    margin-bottom: 5px;

    i {
      font-size: 30px;
      margin-right: .25rem;

      &.red {
        color: #D0021B;
      }

      &.green {
        color: #00BB5F;
      }

      &.orange {
        color: #FF8429;
      }
    }
  }

  .dots-wrapper {
    justify-content: flex-end;
    display: flex;
    line-height: 30px;
    order: 1;
    width: 50%;
    margin-bottom: 10px;
  }

  .pricing {
    order: 2;
    width: auto;
  }

  .subscription {
    order: 3;
    width: auto;
  }

  @media (min-width: 600px) {
    .pricing {
      order: 2;
      width: auto;
    }

    .subscription {
      order: 3;
      width: auto;
    }
  }

  @media (min-width: 820px) {
    flex-wrap: nowrap;
    .status {
      width: auto;
    }
    
    .pricing {
      order: 1;
      width: auto;
    }

    .subscription {
      order: 2;
      width: auto;
    }

    .dots-wrapper {
      order: 3;
      width: auto;
    }
  }
}
</style>